<template>
  <div class="filter-controls">
    <div class="filter-dropdown">
      <div
        class="selected-filters"
        @click="toggleFilterDropdown"
        ref="filterDropdown"
      >
        <span class="filter-label">{{ $t("Custom.filterBy") }}:</span>
        <div class="filter-chips">
          <div
            v-for="filter in selectedFilters"
            :key="filter"
            class="filter-chip"
          >
            {{ getFilterLabel(filter) }}
            <span class="remove-filter" @click.stop="removeFilter(filter)">
              <span class="material-symbols-outlined">close</span>
            </span>
          </div>
          <span v-if="!selectedFilters.length" class="placeholder">
            {{ placeholderText }}
          </span>
        </div>

        <div
          v-if="selectedFilters.length"
          class="clear-filters"
          @click.stop="clearAllFilters"
        >
          <span class="material-symbols-outlined">delete</span>
        </div>

        <span class="dropdown-arrow material-symbols-outlined">
          {{ showFilterDropdown ? "expand_less" : "expand_more" }}
        </span>
      </div>
      <transition name="slide-fade">
        <div v-show="showFilterDropdown" class="filter-options">
          <div
            v-for="option in availableFilters"
            :key="option.id"
            class="filter-option"
            @click.stop="toggleFilter(option.id)"
          >
            <span
              class="material-symbols-outlined check-icon"
              :class="{ selected: selectedFilters.includes(option.id) }"
            >
              {{
                selectedFilters.includes(option.id)
                  ? "check_box"
                  : "check_box_outline_blank"
              }}
            </span>
            {{ option.name }}
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: "MultiSelectFilterComponent",
  props: {
    availableFilters: {
      type: Array,
      required: true,
    },
    placeholderText: {
      type: String,
      default: "All Filters",
    },
    initialSelected: {
      type: Array,
      default: () => [],
    },
    defaultSelected: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showFilterDropdown: false,
      selectedFilters: this.initialSelected.length
        ? [...this.initialSelected]
        : [...this.defaultSelected],
    };
  },
  methods: {
    clearAllFilters() {
      this.selectedFilters = [];
      this.emitFilters();
    },
    toggleFilterDropdown() {
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    toggleFilter(filterId) {
      const index = this.selectedFilters.indexOf(filterId);
      if (index > -1) {
        this.selectedFilters.splice(index, 1);
      } else {
        this.selectedFilters.push(filterId);
      }
      this.emitFilters();
    },
    removeFilter(filterId) {
      const index = this.selectedFilters.indexOf(filterId);
      if (index > -1) {
        this.selectedFilters.splice(index, 1);
        this.emitFilters();
      }
    },
    getFilterLabel(filterId) {
      const filter = this.availableFilters.find((f) => f.id === filterId);
      return filter ? filter.name : filterId;
    },
    emitFilters() {
      this.$emit("update:selected-filters", this.selectedFilters);
    },
    initializeSelectedFilters() {
      if (this.initialSelected.length) {
        this.selectedFilters = [...this.initialSelected];
      } else {
        this.selectedFilters = [...this.defaultSelected];
      }
      this.emitFilters();
    },
    handleClickOutside(event) {
      if (
        this.$refs.filterDropdown &&
        !this.$refs.filterDropdown.contains(event.target)
      ) {
        this.showFilterDropdown = false;
      }
    },
  },
  mounted() {
    this.initializeSelectedFilters();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    initialSelected(newVal) {
      this.selectedFilters = newVal.length
        ? [...newVal]
        : [...this.defaultSelected];
    },
  },
};
</script>

<style scoped>
.filter-dropdown {
  position: relative;
  max-width: 550px;
}

.selected-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid #dfe1e5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.selected-filters:hover {
  border-color: var(--logo-color);
}

.filter-label {
  color: #5f6368;
  font-size: 13px;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex-grow: 1;
  font-size: 12px;
  border-radius: 7px;
}

.filter-chip {
  background: var(--hover-color);
  color: var(--logo-color);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.remove-filter {
  display: flex;
  cursor: pointer;
  opacity: 0.7;
}

.remove-filter span {
  font-size: 14px;
}

.remove-filter:hover {
  opacity: 1;
}

.dropdown-arrow {
  margin-left: 0;
  margin-left: auto;
  transition: transform 0.2s;
}

.filter-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dfe1e5;
  border-radius: 8px;
  margin-top: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
}

.filter-option {
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: background 0.2s;
}

.filter-option:hover {
  background: #f8f9fa;
}

.check-icon {
  color: #5f6368;
}

.check-icon.selected {
  color: var(--logo-color);
}

.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.15s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.clear-filters {
  display: flex;
  cursor: pointer;
  opacity: 0.7;
  color: #5f6368;
}

.clear-filters span {
  font-size: 16px;
}

.clear-filters:hover {
  opacity: 1;
  color: var(--red-color);
}

@media (max-width: 1152px) {
  .filter-controls {
    width: 100%;
  }
  .filter-dropdown {
    max-width: 100%;
  }
}
</style>
